/*
 * Bag-name custom-print charge enforcer.
 *
 * Problem: a bag's "$12.99 custom name" charge is a SEPARATE Shopify line item
 * (variant 45342254170347 of the "CUSTOM TEXT" product). The bag itself carries a
 * line-item property "Custom Name" (or legacy "BAG TEXT" / "BAG NAME") that
 * fulfillment uses to print. Two directions to keep in sync:
 *
 *   1. A customer with a printed name on the bag but no $12.99 charge → would
 *      get free printing. Auto top-up the charge.
 *
 *   2. A customer who REMOVES the $12.99 charge from the cart expects the name
 *      to also be removed (no free printing, no surprise charge either). Auto
 *      clear the bag's name property when no charge is present.
 *
 * Reconcile runs on page load, after every /cart/* mutation, and on tab focus.
 */
(function () {
  'use strict';

  // Match charge lines by product handle, not by variant ID. There are at least
  // two products that represent the $12.99 name-print service ("custom-text" with
  // variants 45342254170347/203115, and the newer "custom-name" with variant
  // 9831156416548 used by the 2025 PDP block). Handle-based matching survives
  // catalog changes and supports both products.
  var CHARGE_PRODUCT_HANDLES = ['custom-name', 'custom-text'];
  var CHARGE_VARIANT_FOR_TOPUP = 9831156416548; // current PDP default
  var BAG_NAME_KEYS = ['Custom Name', 'BAG TEXT', 'BAG NAME'];
  var THROTTLE_MS = 400;
  var inFlight = false;
  var lastRun = 0;

  function isChargeItem(item) {
    return CHARGE_PRODUCT_HANDLES.indexOf(item.handle) !== -1;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function bagNameOf(item) {
    var props = item.properties || {};
    for (var i = 0; i < BAG_NAME_KEYS.length; i++) {
      var v = props[BAG_NAME_KEYS[i]];
      if (v != null && String(v).trim() !== '') return String(v);
    }
    return null;
  }

  function countBagsNeedingCharge(cart) {
    return cart.items.reduce(function (total, item) {
      if (isChargeItem(item)) return total;
      if (item.properties && item.properties['_Order_ID']) return total;
      return bagNameOf(item) ? total + item.quantity : total;
    }, 0);
  }

  function chargeLines(cart) {
    return cart.items.filter(isChargeItem);
  }

  function totalChargeQty(cart) {
    return chargeLines(cart).reduce(function (s, it) { return s + it.quantity; }, 0);
  }

  function firstBagName(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isChargeItem(cart.items[i])) continue;
      var n = bagNameOf(cart.items[i]);
      if (n) return n;
    }
    return '';
  }

  function topUp(deficit, cart) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: CHARGE_VARIANT_FOR_TOPUP,
          quantity: deficit,
          properties: { 'Custom Name': firstBagName(cart) }
        }]
      })
    });
  }

  function trim(excess, cart) {
    var lines = chargeLines(cart);
    var remaining = excess;
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        if (remaining <= 0) return;
        var newQty = Math.max(0, line.quantity - remaining);
        var consumed = line.quantity - newQty;
        remaining -= consumed;
        return fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: newQty })
        });
      });
    });
    return chain;
  }

  // Clear the bag's "Custom Name" / "BAG TEXT" / "BAG NAME" properties.
  //
  // IMPORTANT: Shopify's Ajax cart API does NOT support changing line item
  // properties via /cart/change.js. Per the official docs:
  //   "You can't change a line item's properties or product. Instead, remove
  //    the existing line item and add a new line item with the updated
  //    information."
  //   https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
  //
  // So we do an add-new-then-remove-old swap. Order matters: add the cleaned
  // line FIRST so the bag is never absent from the cart (preserves any
  // surrounding state and avoids a flash of empty cart), THEN remove the old
  // line by key.
  function clearBagNames(cart) {
    var chain = Promise.resolve();
    cart.items.forEach(function (item) {
      if (isChargeItem(item)) return;
      if (item.properties && item.properties['_Order_ID']) return;
      if (!bagNameOf(item)) return;

      // Filter out only the name keys; keep everything else (uploads, notes…).
      var cleanedProps = {};
      var props = item.properties || {};
      Object.keys(props).forEach(function (k) {
        if (BAG_NAME_KEYS.indexOf(k) === -1) cleanedProps[k] = props[k];
      });

      var addBody = {
        items: [{
          id: item.variant_id,
          quantity: item.quantity,
          properties: cleanedProps
        }]
      };
      // Preserve selling plan if present so subscription bags don't lose their plan.
      if (item.selling_plan_allocation &&
          item.selling_plan_allocation.selling_plan &&
          item.selling_plan_allocation.selling_plan.id) {
        addBody.items[0].selling_plan = item.selling_plan_allocation.selling_plan.id;
      }

      chain = chain
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(addBody)
          });
        })
        .then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
        });
    });
    return chain;
  }

  function reconcile() {
    var now = Date.now();
    if (inFlight || now - lastRun < THROTTLE_MS) return Promise.resolve();
    lastRun = now;
    inFlight = true;

    return getCart()
      .then(function (cart) {
        var need = countBagsNeedingCharge(cart);
        var have = totalChargeQty(cart);
        // Reactive safety net is conservative: only handle the "charge was
        // removed but bag still has a printed-name property" case (which is the
        // user-visible bug). Do NOT trim excess charges or auto-top-up — those
        // are dangerous heuristics that can race with the legacy two-step add
        // queue and accidentally remove the customer's just-added charge line.
        // The deterministic path is window.PedalCartRemove, invoked from the
        // Remove button itself.
        if (have === 0 && need > 0) return clearBagNames(cart);
        return null;
      })
      .then(function (mutation) {
        if (mutation && typeof window.dispatchEvent === 'function') {
          // Background-reconciliation safety net only — do NOT dispatch cart:build
          // here. The explicit PedalCartRemove handler refreshes the UI itself,
          // and dispatching cart:build from a debounced timer would cause a
          // second drawer rebuild after the customer's click.
          window.dispatchEvent(new CustomEvent('cart:bag-name-enforcer:reconciled'));
        }
      })
      .catch(function (err) { console.error('[bag-name-enforcer]', err); })
      .then(function () { inFlight = false; });
  }

  var CART_MUTATION_RE = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/;

  // Debounce reconcile so multi-step Shopify.queue add flows (custom-name
  // product, then the bag) settle into a single consistent cart state before we
  // inspect it. Without this, the intermediate state (charge added, bag not
  // yet) looks like excess charge and the enforcer trims it.
  var DEBOUNCE_MS = 800;
  var debounceTimer = null;
  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { debounceTimer = null; reconcile(); }, DEBOUNCE_MS);
  }

  // Hook fetch so any /cart mutation triggers a reconcile after settling.
  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation = CART_MUTATION_RE.test(url);
    var ours = isCartMutation && init && init.headers &&
      JSON.stringify(init.headers).indexOf('bag-name-enforcer') >= 0;
    var promise = originalFetch.apply(this, arguments);
    if (isCartMutation && !ours) {
      promise.then(scheduleReconcile).catch(function () {});
    }
    return promise;
  };

  // Note: an XHR-prototype hook was tried for $.ajax cart mutations, but it
  // raced with the legacy two-step Shopify.queue add flow (custom-name product
  // → bag) and caused the just-added charge to be trimmed. The deterministic
  // remove path is window.PedalCartRemove below, invoked by the Remove button
  // directly, so reactive XHR observation is no longer needed.

  // ---------------------------------------------------------------------------
  // Atomic remove handler invoked by the cart-item Remove button. By doing the
  // bag-name clear and the charge removal sequentially in a single click, we
  // dispatch cart:build exactly ONCE — no flashing, no race with the debounced
  // reconcile. cart-item.liquid calls this if defined; otherwise falls back to
  // its own $.ajax remove.
  // ---------------------------------------------------------------------------
  window.PedalCartRemove = function (btn) {
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    var isCharge = btn.getAttribute('data-charge-line') === 'true';
    if (!variantId) return;

    var rowEl = btn.closest && btn.closest('[data-key]');

    function refreshUI() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var sub = document.querySelector('[data-subtotal]');
          if (sub && window.theme && theme.Currency) {
            sub.innerHTML = theme.Currency.formatMoney(data.total_price, theme.settings.moneyFormat);
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
          document.dispatchEvent(new CustomEvent('cart:build'));
        });
    }

    function removeCharge() {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      });
    }

    var chain;
    if (isCharge) {
      // Sequence: get cart → clear bag Custom Name on each bag line → remove charge → refresh UI
      chain = getCart().then(function (cart) {
        return clearBagNames(cart);
      }).then(removeCharge);
    } else {
      chain = removeCharge();
    }

    chain.then(function () {
      if (rowEl) rowEl.remove();
      return refreshUI();
    }).catch(function (err) {
      console.error('[PedalCartRemove]', err);
    });
  };

  // Initial reconcile + on tab focus (covers customers returning after editing cart elsewhere).
  function init() {
    reconcile();
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) reconcile();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
